import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { MonitoringActions } from './monitoring.action-types';
import {
  MonitoringModels,
} from '../../../pages/monitoring/models/monitoring.models';
import { AlertV2SelectedAction } from '@models/alerts-v2.model';
import AccountsPages = MonitoringModels.AccountsPages;

export declare interface MonitoringState extends EntityState<MonitoringModels.Account> {
  isCreating: boolean;
  isDeleting: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  currentPage: AccountsPages;
  isContactsLoading: boolean;
  isOrgContactsLoading: boolean;
  isScheduleLoading: boolean;
  isPartitionScheduleLoading: boolean;
  isZonesLoading: boolean;
  isFormDetailsValid: boolean;
  isPartitionFormValid: boolean;
  initialLoading: boolean;
  deletingAccountsIds: string[];
  account: MonitoringModels.Account;
  isCreatingProcess: boolean;
  isEditingProcess: boolean;
  currentStep: number;
  contactGroups: MonitoringModels.ContactGroup[];
  selectedAccountContacts: MonitoringModels.AccountContact[];
  currentAccountSchedule: MonitoringModels.AccountSchedule;
  partitions: MonitoringModels.Partition[];
  currentPartition: MonitoringModels.Partition;
  partitionSchedule: MonitoringModels.AccountSchedule;
  currentPartitionId: string;
  selectedZones: MonitoringModels.ZoneModel;
  allOrganizationContacts: MonitoringModels.OrganizationContact[];
  grantedOrganizations: MonitoringModels.GrantedOrganization[];
  selectedPolicyTemplate: { category: number; type: number } | null;
  policyFlow: MonitoringModels.PolicyFlow;
  policyActions: AlertV2SelectedAction[] | null;
  policyFormValid: boolean;
  policies: MonitoringModels.Policy[];
}

export const adapter: EntityAdapter<MonitoringModels.Account> =
  createEntityAdapter<MonitoringModels.Account>({
    selectId: (account: MonitoringModels.Account) => account._id,
  });

const initialState: MonitoringState = adapter.getInitialState({
  isCreating: false,
  isDeleting: false,
  isLoading: false,
  isUpdating: false,
  currentPage: null,
  isContactsLoading: false,
  isOrgContactsLoading: false,
  isScheduleLoading: false,
  isPartitionScheduleLoading: false,
  isZonesLoading: false,
  initialLoading: false,
  isCreatingProcess: false,
  isEditingProcess: false,
  isFormDetailsValid: false,
  isPartitionFormValid: false,
  deletingAccountsIds: [],
  currentStep: null,
  account: null,
  contactGroups: null,
  selectedAccountContacts: null,
  currentAccountSchedule: null,
  partitions: null,
  currentPartition: null,
  partitionSchedule: null,
  currentPartitionId: null,
  selectedZones: null,
  allOrganizationContacts: null,
  grantedOrganizations: null,
  selectedPolicyTemplate: null,
  policyFlow: null,
  policyActions: null,
  policyFormValid: false,
  policies: null,
});

const { selectAll } = adapter.getSelectors();

export const monitoringReducer = createReducer(
  initialState,
  on(MonitoringActions.resetToInitialState, (state) => {
    return {
      ...initialState,
    };
  }),
  on(MonitoringActions.getMonitoringAccountsSuccess, (state, { accounts }) => {
    return adapter.setAll(accounts, {
      ...state,
    });
  }),
  on(MonitoringActions.updateGroup, (state, { groupId, name }) => ({
    ...state,
    contactGroups: state.contactGroups
      ? state.contactGroups.map((g) =>
        g._id === groupId ? { ...g, name } : g,
      )
      : state.contactGroups,
  })),
  on(MonitoringActions.setDisable, (state, { accountId }) => {
    const id = accountId;

    const next = adapter.updateOne(
      { id, changes: { enabled: 0, effectiveArmed: false } },
      state,
    );

    return {
      ...next,
    };
  }),
  on(MonitoringActions.reorderGroupContactsSuccess, (state, { orderedContactsIds, groupId }) => {
    const currentContacts = state.selectedAccountContacts ?? [];

    const contactsMap = new Map<string, MonitoringModels.AccountContact>();
    currentContacts.forEach(contact => {
      if (String(contact.groupId) === String(groupId)) {
        contactsMap.set(String(contact.contactId), contact);
      }
    });

    const updatedContacts = currentContacts.map(contact => {
      if (String(contact.groupId) !== String(groupId)) {
        return contact;
      }

      const newIndex = orderedContactsIds.findIndex(
        id => String(id) === String(contact.contactId)
      );

      if (newIndex === -1) {
        return contact;
      }

      return {
        ...contact,
        orderIndex: newIndex,
      };
    });

    return {
      ...state,
      selectedAccountContacts: updatedContacts,
    };
  }),
  on(MonitoringActions.startUpdatePolicySuccess, (state, { policyId, policyData }) => {
    return {
      ...state,
      policies: state.policies
        ? state.policies.map((p) =>
          String(p._id) === String(policyId)
            ? {
              ...p,
              ...policyData,
              _id: p._id,
              accountId: p.accountId,
              orgId: p.orgId,
              partitionId: p.partitionId,
              createdAt: p.createdAt,
              updatedAt: p.updatedAt,
            }
            : p,
        )
        : state.policies,
    };
  }),
  on(MonitoringActions.startDeletePolicySuccess, (state, { policyId }) => {
    const policies = state.policies.filter(policy => policy._id !== policyId);

    return {
      ...state,
      policies,
    }
  }),
  on(MonitoringActions.setEnable, (state, { accountId }) => {
    const id = accountId;

    const next = adapter.updateOne(
      { id, changes: { enabled: 1, effectiveArmed: true } },
      state,
    );

    return {
      ...next,
    };
  }),
  on(
    MonitoringActions.setIsPartitionScheduleLoading,
    (state, { isPartitionScheduleLoading }) => {
      return {
        ...state,
        isPartitionScheduleLoading,
      };
    },
  ),
  on(MonitoringActions.enablePartitionSuccess, (state, { partitionId }) => {
    const id = partitionId;

    const baseState = {
      ...state,
      partitions: state.partitions
        ? state.partitions.map((p) =>
          p._id === id ? { ...p, enabled: true, effectiveArmed: true } : p,
        )
        : state.partitions,
      currentPartition:
        state.currentPartition && state.currentPartition._id === id
          ? { ...state.currentPartition, enabled: true, effectiveArmed: true }
          : state.currentPartition,
    };

    const accountUpdates = (state.ids as string[])
      .map((aId) => {
        const account = state.entities[aId];
        if (!account?.partitions?.length) return null;
        const hasMatch = account.partitions.some((p) => p._id === id);
        if (!hasMatch) return null;
        const partitions = account.partitions.map((p) =>
          p._id === id ? { ...p, enabled: true, effectiveArmed: true } : p
        );
        return { id: aId, changes: { partitions } };
      })
      .filter((u): u is { id: string; changes: { partitions: MonitoringModels.Partition[] } } => u != null);

    if (accountUpdates.length === 0) return baseState;
    return adapter.updateMany(accountUpdates, baseState);
  }),
  on(MonitoringActions.disablePartitionSuccess, (state, { partitionId }) => {
    const id = partitionId;

    const baseState = {
      ...state,
      partitions: state.partitions
        ? state.partitions.map((p) =>
          p._id === id ? { ...p, enabled: false, effectiveArmed: false } : p,
        )
        : state.partitions,
      currentPartition:
        state.currentPartition && state.currentPartition._id === id
          ? { ...state.currentPartition, enabled: false, effectiveArmed: false }
          : state.currentPartition,
    };

    const accountUpdates = (state.ids as string[])
      .map((aId) => {
        const account = state.entities[aId];
        if (!account?.partitions?.length) return null;
        const hasMatch = account.partitions.some((p) => p._id === id);
        if (!hasMatch) return null;
        const partitions = account.partitions.map((p) =>
          p._id === id ? { ...p, enabled: false, effectiveArmed: false } : p
        );
        return { id: aId, changes: { partitions } };
      })
      .filter((u): u is { id: string; changes: { partitions: MonitoringModels.Partition[] } } => u != null);

    if (accountUpdates.length === 0) return baseState;
    return adapter.updateMany(accountUpdates, baseState);
  }),
  on(MonitoringActions.setCurrentStep, (state, { step }) => {
    return {
      ...state,
      currentStep: step,
    };
  }),
  on(MonitoringActions.setPartitionPolicies, (state, { policies }) => {
    return {
      ...state,
      policies,
    };
  }),
  on(MonitoringActions.setSelectedPolicyTemplate, (state, { template }) => {
    return {
      ...state,
      selectedPolicyTemplate: template,
    };
  }),
  on(MonitoringActions.setPolicyFlow, (state, { policyFlow }) => {
    return {
      ...state,
      policyFlow,
    };
  }),
  on(MonitoringActions.startCreatePolicySuccess, (state, { policy }) => {
    return {
      ...state,
      policies: [ ...state.policies, policy ],
    };
  }),
  on(MonitoringActions.startDuplicatePolicySuccess, (state, { policy }) => {
    return {
      ...state,
      policies: [...state.policies, policy],
    };
  }),
  on(MonitoringActions.setPolicyActions, (state, { actions }) => {
    return {
      ...state,
      policyActions: actions,
    };
  }),
  on(MonitoringActions.setPolicyFormValid, (state, { valid }) => {
    return {
      ...state,
      policyFormValid: valid,
    };
  }),
  on(MonitoringActions.setIsEditPartition, (state, { isEditPartition }) => {
    return {
      ...state,
      isEditPartition,
    };
  }),
  on(MonitoringActions.createGrantedOrganizationsSuccess, (state, { organization }) => {
    return {
      ...state,
      grantedOrganizations: [...state.grantedOrganizations, organization],
    };
  }),
  on(MonitoringActions.removeCurrentAccount, (state) => {
    return {
      ...state,
      account: null,
      selectedAccountContacts: null,
      currentAccountSchedule: null,
    };
  }),
  on(MonitoringActions.createContactGroupSuccess, (state, { group }) => {
    return {
      ...state,
      contactGroups: [...state.contactGroups, group],
    };
  }),
  on(
    MonitoringActions.setPartitionSchedule,
    (state, { partitionSchedule }) => {
      return {
        ...state,
        partitionSchedule,
      };
    },
  ),
  on(
    MonitoringActions.savePartitionScheduleSuccess,
    (state, { accountSchedule }) => {
      return {
        ...state,
        partitionSchedule: { ...accountSchedule},
      };
    },
  ),
  on(MonitoringActions.startSaveZonesSuccess, (state, { zones }) => {
    return {
      ...state,
      selectedZones: zones,
    };
  }),
  on(MonitoringActions.startDeleteContactSuccess, (state, { contactId, groupId }) => {
    const selectedAccountContacts = (state.selectedAccountContacts ?? []).filter(
      (c) => !(String(c.contactId) === contactId && String(c.groupId) === groupId),
    );

    const contactGroups = (state.contactGroups ?? []).map((g) =>
      String(g._id) === groupId
        ? { ...g, contacts: (g.contacts ?? []).filter((c) => String(c.contactId) !== contactId) }
        : g,
    );

    return {
      ...state,
      selectedAccountContacts,
      contactGroups,
    };
  }),
  on(MonitoringActions.startDeleteContactGroupSuccess, (state, { groupId }) => {
    const groups = state.contactGroups.filter(
      (group) => String(group._id) !== groupId,
    );
    return {
      ...state,
      contactGroups: groups,
    };
  }),
  on(MonitoringActions.startDeletePartitionSuccess, (state, { partitionId }) => {
    const partitions = state.partitions.filter(
      (partition) => partition._id !== partitionId,
    );
    return {
      ...state,
      partitions,
    };
  }),
  on(
    MonitoringActions.sendCreateContactRequestSuccess,
    (state, { groupId, contact }) => {
      if (!contact?.length) return state;

      const group = (state.contactGroups ?? []).find(
        (g) => String(g._id) === String(groupId),
      );
      const currentInGroup = group?.contacts ?? [];
      const minOrder =
        currentInGroup.length > 0
          ? Math.min(
              ...currentInGroup.map((c) =>
                typeof (c as MonitoringModels.AccountContact).orderIndex ===
                'number'
                  ? (c as MonitoringModels.AccountContact).orderIndex
                  : 0,
              ),
              0,
            )
          : 0;
      const newContactsWithOrder = contact.map((c, i) => ({
        ...c,
        orderIndex: minOrder - 1 - i,
      }));

      const nextContactGroups = (state.contactGroups ?? []).map((g) => {
        if (String(g._id) !== String(groupId)) return g;
        const current = g.contacts ?? [];
        return {
          ...g,
          contacts: [...newContactsWithOrder, ...current],
        };
      });

      const currentContacts = state.selectedAccountContacts ?? [];
      return {
        ...state,
        selectedAccountContacts: [...newContactsWithOrder, ...currentContacts],
        contactGroups: nextContactGroups,
      };
    },
  ),
  on(MonitoringActions.createAccountRequestSuccess, (state, { account }) => {
    return adapter.setOne(account, {
      ...state,
      account,
    });
  }),
  on(
    MonitoringActions.getCurrentPartitionSuccess,
    (state, { partition }) => {
      return {
        ...state,
        currentPartition: partition,
      };
    },
  ),
  on(MonitoringActions.sendUpdatePartitionRequestSuccess, (state, { partitionDetails }) => {
    const updatedPartition = {
      ...state.currentPartition,
      ...partitionDetails,
    };

    return {
      ...state,
      currentPartition: updatedPartition,
      partitions: state.partitions
        ? state.partitions.map((p) =>
          p._id === updatedPartition._id
            ? { ...p, ...partitionDetails }
            : p,
        )
        : state.partitions,
    };
  }),
  on(MonitoringActions.startDeleteOrgContactSuccess, (state, { contactId }) => {
    const orgContacts = state.allOrganizationContacts.filter(c => c._id !== contactId);

    return {
      ...state,
      allOrganizationContacts: orgContacts,
    };
  }),
  on(MonitoringActions.updateAccountScheduleSuccess, (state, { accountSchedule }) => {
    if (!accountSchedule) return state;

    return {
      ...state,
      currentAccountSchedule: accountSchedule,
    };
  }),
  on(MonitoringActions.startCreateOrgContactSuccess, (state, { contact }) => {
    return {
      ...state,
      allOrganizationContacts: [...state.allOrganizationContacts, contact],
    };
  }),
  on(MonitoringActions.setCurrentAccountId, (state, { id }) => {
    return {
      ...state,
      currentPartitionId: id,
    };
  }),
    on(MonitoringActions.startDeleteOrgContactSuccess, (state, { contactId }) => {
      const contacts = state.allOrganizationContacts.filter(c => String(c._id) !== contactId);

      return {
        ...state,
        allOrganizationContacts: [...contacts],
      };
    }),
  on(MonitoringActions.setIsFormDetailsValid, (state, { isFormDetailsValid }) => {
    return {
      ...state,
      isFormDetailsValid,
    };
  }),
  on(MonitoringActions.setIsPartitionFormValid, (state, { isPartitionFormValid }) => {
    return {
      ...state,
      isPartitionFormValid,
    };
  }),
  on(MonitoringActions.getGrantedOrganizationsSuccess, (state, { grantedOrganizations }) => {
    return {
      ...state,
      grantedOrganizations,
    };
  }),
  on(MonitoringActions.deleteGrantedOrganizationsSuccess, (state, { orgId }) => {
    const grantedOrg = state.grantedOrganizations.filter((org) => org.granteeOrgId !== orgId);

    return {
      ...state,
      grantedOrganizations: grantedOrg,
    };
  }),
  on(MonitoringActions.sendCreatePartitionRequestSuccess, (state, { partition }) => {
    return {
      ...state,
      partitions: [...state.partitions, partition],
    };
  }),
  on(MonitoringActions.setInitialLoading, (state, { initialLoading }) => {
    return {
      ...state,
      initialLoading,
    };
  }),
  on(MonitoringActions.setCurrentPage, (state, { page }) => {
    return {
      ...state,
      currentPage: page,
    };
  }),
  on(MonitoringActions.setSelectedZones, (state, { zones }) => {
    return {
      ...state,
      selectedZones: zones,
    };
  }),
  on(
    MonitoringActions.setCurrentAccountSchedule,
    (state, { currentAccountSchedule }) => {
      return {
        ...state,
        currentAccountSchedule,
      };
    },
  ),
  on(MonitoringActions.getAllOrganizationContacts, (state) => ({
    ...state,
    isOrgContactsLoading: true,
  })),
  on(
    MonitoringActions.getAllOrganizationContactsSuccess,
    (state, { allOrganizationContacts }) => {
      return {
        ...state,
        allOrganizationContacts,
        isOrgContactsLoading: false,
      };
    },
  ),
  on(MonitoringActions.getAllOrganizationContactsFail, (state) => ({
    ...state,
    isOrgContactsLoading: false,
  })),
  on(MonitoringActions.setIsCreatingProcess, (state, { isCreatingProcess }) => {
    return {
      ...state,
      isCreatingProcess,
    };
  }),
  on(MonitoringActions.getMonitoringContactsSuccess, (state, { contacts }) => {
    return {
      ...state,
      selectedAccountContacts: contacts,
    };
  }),
  on(MonitoringActions.setAccountContactGroups, (state, { contactGroups }) => {
    return {
      ...state,
      contactGroups,
    };
  }),
  on(MonitoringActions.setIsLoading, (state, { isLoading }) => {
    return {
      ...state,
      isLoading,
    };
  }),
  on(MonitoringActions.setIsUpdating, (state, { isUpdating }) => {
    return {
      ...state,
      isUpdating,
    };
  }),
  on(MonitoringActions.setIsZonesLoading, (state, { isZonesLoading }) => {
    return {
      ...state,
      isZonesLoading,
    };
  }),
  on(MonitoringActions.setIsDeleting, (state, { isDeleting }) => {
    return {
      ...state,
      isDeleting,
    };
  }),
  on(
    MonitoringActions.startUpdateOrgContactSuccess,
    (state, { contactId, contactInfo, vacation }) => {
      return {
        ...state,
        allOrganizationContacts: state.allOrganizationContacts.map((contact) =>
          contact._id === contactId ? { ...contact, ...contactInfo, vacation: vacation ? [...vacation] : [] } : contact,
        ),
      };
    },
  ),
  on(MonitoringActions.setIsContactsLoading, (state, { isContactsLoading }) => {
    return {
      ...state,
      isContactsLoading,
    };
  }),
  on(MonitoringActions.setIsScheduleLoading, (state, { isScheduleLoading }) => {
    return {
      ...state,
      isScheduleLoading,
    };
  }),
  on(MonitoringActions.setIsCreatingLoader, (state, { isCreating }) => {
    return {
      ...state,
      isCreating,
    };
  }),
  on(MonitoringActions.setIsEditingProcess, (state, { isEditingProcess }) => {
    return {
      ...state,
      isEditingProcess,
    };
  }),
  on(MonitoringActions.updateAccountRequestSuccess, (state, { id, accountInfo }) => {
    const accountId = id;

    const next = adapter.updateOne(
      { id: accountId, changes: { ...accountInfo } },
      state,
    );

    return {
      ...next,
      account:
        state.account && state.account._id === accountId
          ? ({ ...state.account, ...accountInfo })
          : state.account,
    };
  }),
  on(MonitoringActions.setPartitions, (state, { partitions }) => {
    return {
      ...state,
      partitions,
    };
  }),
  on(MonitoringActions.getOneAccountSuccess, (state, { account }) => {
    return {
      ...state,
      account,
    };
  }),
  on(MonitoringActions.startDeleteAccount, (state, { id }) => {
    return {
      ...state,
      deletingAccountsIds: [...state.deletingAccountsIds, id],
    };
  }),
  on(MonitoringActions.sendDeleteAccountRequestSuccess, (state, { id }) => {
    const deletingIndex = state.deletingAccountsIds.findIndex(
      (_id) => _id === id,
    );
    let newDeletingArray = [...state.deletingAccountsIds];
    newDeletingArray.splice(deletingIndex, 1);
    return adapter.removeOne(id, {
      ...state,
      deletingAccountsIds: newDeletingArray,
      notEmpty: selectAll(state).length > 1,
    });
  }),
  on(MonitoringActions.sendDeleteAccountRequestFail, (state, { id }) => {
    const deletingIndex = state.deletingAccountsIds.findIndex(
      (_id) => _id === id,
    );
    let newDeletingArray = [...state.deletingAccountsIds];
    newDeletingArray.splice(deletingIndex, 1);
    return {
      ...state,
      deletingAccountsIds: newDeletingArray,
    };
  }),
);
